import React, {FC, useEffect, useState} from "react";
import {Modal, Select, Input, Alert} from "antd";
import moment, {Moment} from "moment";
import {PaymentTransferType} from "../../../../generated/graphql";
import {renderAmount} from "../../../../helpers/renderAmount";
import DateInput from "../../../../views/DateInput/DateInput";

const {Option} = Select;
const {TextArea} = Input;

// ─── "Außerhalb Mollie bezahlt erfassen" — Modal ─────────────────────────────
//
// Anwendungsfall: Der Mollie-Zahlungslink ist defekt (z.B. abgelaufen/fehlerhaft)
// und der Kunde hat extern bezahlt (Überweisung/PayPal/Bar/Kreditkarte). Der
// Mitarbeiter erfasst die Zahlung direkt an der Rate. Es wird IMMER der volle
// offene Ratenbetrag erfasst — das Betragsfeld ist daher reine Anzeige (read-only)
// und es wird KEIN Betrag an die Mutation übergeben (das Backend nimmt den vollen
// Ratenbetrag).
//
// Dieser Ordner nutzt bewusst KEINE i18n — alle Texte sind hartkodiertes Deutsch
// mit echten Umlauten (analog der Geschwister-Komponenten in MolliePaymentSection).
//
// Präsentational: KEIN Apollo-Hook hier — die Mutation liegt in der ActionBar.

export interface RecordExternalPaymentValues {
    paymentTransferType: PaymentTransferType;
    /** ISO-8601 String (moment().toISOString()). */
    paymentDone: string;
    reference?: string;
}

export interface RecordExternalPaymentModalProps {
    visible: boolean;
    /** Voller offener Ratenbetrag (nur Anzeige). */
    openAmount: number;
    loading: boolean;
    onCancel: () => void;
    onConfirm: (values: RecordExternalPaymentValues) => void;
}

// Genau diese vier Zahlungsarten, in dieser Reihenfolge, mit deutschen Labels.
const TRANSFER_TYPE_OPTIONS: ReadonlyArray<{value: PaymentTransferType; label: string}> = [
    {value: PaymentTransferType.BankTransfer, label: "Überweisung"},
    {value: PaymentTransferType.Paypal, label: "PayPal"},
    {value: PaymentTransferType.Cash, label: "Bar"},
    {value: PaymentTransferType.Creditcard, label: "Kreditkarte"},
];

const HINT_TEXT =
    "Erfasst eine außerhalb von Mollie eingegangene Zahlung (z. B. Überweisung/PayPal). "
    + "Die Rate wird als vollständig bezahlt markiert; ein noch aktiver Mollie-Zahlungsversuch "
    + "wird dabei automatisch storniert bzw. entwertet, damit keine Doppelzahlung möglich ist.";

// Bewusst ein <div> statt <label> (kein form-control-binding noetig; vermeidet
// jsx-a11y/label-has-associated-control) — die antd-Controls tragen eigene Labels/
// data-testids.
const labelStyle: React.CSSProperties = {
    display: "block",
    marginBottom: 4,
    fontWeight: 600,
};

const fieldStyle: React.CSSProperties = {
    marginBottom: 16,
};

export const RecordExternalPaymentModal: FC<RecordExternalPaymentModalProps> = ({
    visible,
    openAmount,
    loading,
    onCancel,
    onConfirm,
}) => {
    const [transferType, setTransferType] = useState<PaymentTransferType>(
        PaymentTransferType.BankTransfer,
    );
    const [paymentDate, setPaymentDate] = useState<Moment>(moment());
    const [reference, setReference] = useState<string>("");

    // Felder bei jedem Öffnen auf die Defaults zurücksetzen (das Komponenten-
    // State überlebt destroyOnClose, da die Komponente selbst dauerhaft gemountet
    // ist — nur der Modal-Inhalt wird zerstört).
    useEffect(() => {
        if (visible) {
            setTransferType(PaymentTransferType.BankTransfer);
            setPaymentDate(moment());
            setReference("");
        }
    }, [visible]);

    function handleOk() {
        const trimmed = reference.trim();
        onConfirm({
            paymentTransferType: transferType,
            paymentDone: (paymentDate || moment()).toISOString(),
            reference: trimmed.length > 0 ? trimmed : undefined,
        });
    }

    return (
        <Modal
            title="Außerhalb Mollie bezahlt erfassen"
            visible={visible}
            onOk={handleOk}
            onCancel={onCancel}
            okText="Erfassen"
            cancelText="Abbrechen"
            confirmLoading={loading}
            okButtonProps={{disabled: loading}}
            destroyOnClose
            data-testid="record-external-payment-modal"
        >
            <Alert
                type="info"
                showIcon
                style={{marginBottom: 16}}
                message={HINT_TEXT}
            />

            <div style={fieldStyle}>
                <div style={labelStyle}>Zahlungsart</div>
                <Select<PaymentTransferType>
                    value={transferType}
                    onChange={(value) => setTransferType(value)}
                    style={{width: "100%"}}
                    data-testid="record-external-payment-type-select"
                >
                    {TRANSFER_TYPE_OPTIONS.map((opt) => (
                        <Option key={opt.value} value={opt.value}>
                            {opt.label}
                        </Option>
                    ))}
                </Select>
            </div>

            <div style={fieldStyle}>
                <div style={labelStyle}>Zahlungsdatum</div>
                <DateInput
                    value={paymentDate}
                    onChange={(date) => setPaymentDate(date || moment())}
                    displayFormat="DD.MM.YYYY"
                    allowClear={false}
                    style={{width: "100%"}}
                    data-testid="record-external-payment-date"
                />
            </div>

            <div style={fieldStyle}>
                <div style={labelStyle}>Betrag</div>
                {/* Nur Anzeige: es wird IMMER der volle offene Ratenbetrag erfasst. */}
                <Input
                    value={renderAmount({amount: openAmount})}
                    disabled
                    data-testid="record-external-payment-amount"
                />
            </div>

            <div>
                <div style={labelStyle}>Referenz / Notiz (optional)</div>
                <TextArea
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    rows={3}
                    placeholder="z. B. PayPal-Transaktion, Überweisungs-Verwendungszweck"
                    data-testid="record-external-payment-reference"
                />
            </div>
        </Modal>
    );
};

export default RecordExternalPaymentModal;
